/**
 * Format price to Indian Rupee format
 */
export const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(price);
};
/**
 * Format date to readable format
 */
export const formatDate = (date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(d);
};
/**
 * Format date with time
 */
export const formatDateTime = (date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(d);
};
/**
 * Generate SEO-friendly URL slug
 */
export const generateSlug = (text) => {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
};
/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength) => {
    if (text.length <= maxLength)
        return text;
    return text.substring(0, maxLength - 3) + '...';
};
/**
 * Calculate discount percentage
 */
export const calculateDiscountPercentage = (originalPrice, discountedPrice) => {
    if (originalPrice <= 0)
        return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
};
/**
 * Format phone number
 */
export const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
        return `+91 ${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
    }
    return phone;
};
/**
 * Validate email
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};
/**
 * Validate phone number (Indian)
 */
export const isValidPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10 && /^[6-9]/.test(cleaned);
};
/**
 * Validate pincode (Indian)
 */
export const isValidPincode = (pincode) => {
    return /^[1-9][0-9]{5}$/.test(pincode);
};
/**
 * Generate random string
 */
export const generateRandomString = (length) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};
/**
 * Generate order number
 */
export const generateOrderNumber = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0');
    return `ORD${timestamp}${random}`;
};
/**
 * Calculate order totals
 */
export const calculateOrderTotals = (subtotal, discount, shippingCharges, gstPercentage) => {
    const taxableValue = subtotal - discount + shippingCharges;
    const tax = (taxableValue * gstPercentage) / 100;
    const total = taxableValue + tax;
    return {
        subtotal,
        discount,
        afterDiscount: subtotal - discount,
        shippingCharges,
        tax,
        total: Math.round(total * 100) / 100,
    };
};
/**
 * Debounce function
 */
export const debounce = (func, wait) => {
    let timeout = null;
    return (...args) => {
        if (timeout)
            clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};
/**
 * Deep clone object
 */
export const deepClone = (obj) => {
    try {
        return JSON.parse(JSON.stringify(obj));
    }
    catch {
        if (typeof structuredClone === 'function') {
            try {
                return structuredClone(obj);
            }
            catch (e) {
                throw new Error('Deep clone failed: object cannot be cloned');
            }
        }
        else {
            throw new Error('Deep clone failed: structuredClone is not available');
        }
    }
};
/**
 * Check if object is empty
 */
export const isEmpty = (obj) => {
    return Object.keys(obj).length === 0;
};
/**
 * Get file extension
 */
export const getFileExtension = (filename) => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
};
/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0)
        return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};
export const generateSearchKeywords = (title) => {
    // Normalize: lowercase, remove extra spaces, punctuation
    const normalized = title
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .trim();
    // Split into words
    const words = normalized.split(/\s+/);
    // Generate all possible tokens:
    // - Individual words
    // - All possible 2-word combinations (consecutive and non-consecutive)
    // - All possible 3-word combinations (consecutive and non-consecutive)
    // - Full title
    const keywords = new Set();
    // Add individual words
    words.forEach(word => keywords.add(word));
    // Add all 2-word combinations (both consecutive and non-consecutive)
    for (let i = 0; i < words.length; i++) {
        for (let j = i + 1; j < words.length; j++) {
            keywords.add(`${words[i]} ${words[j]}`);
        }
    }
    // Add all 3-word combinations (both consecutive and non-consecutive)
    for (let i = 0; i < words.length; i++) {
        for (let j = i + 1; j < words.length; j++) {
            for (let k = j + 1; k < words.length; k++) {
                keywords.add(`${words[i]} ${words[j]} ${words[k]}`);
            }
        }
    }
    // Add full title
    keywords.add(normalized);
    return Array.from(keywords);
};
